/**
 * Utility functions for WhatsApp message formatting
 */

import React from 'react';
import { Text } from '@chakra-ui/react';

/**
 * Format phone number for WhatsApp
 * @param {string} phone - Phone number to format
 * @returns {string} Formatted phone number
 */
export const formatPhoneForWhatsApp = (phone) => {
  if (!phone) return '';
  
  // If the phone already has a "+" prefix, convert to WhatsApp format
  if (phone.startsWith('+')) {
    // Remove the "+" and return the number
    return phone.substring(1);
  }
  
  // Remove all non-digits
  const cleanPhone = phone.replace(/\D/g, '');
  
  // If the number is longer than 10 digits, assume it already includes a country code
  if (cleanPhone.length > 10) {
    return cleanPhone;
  }
  
  // Default: Assume it's an Indian number if it's 10 digits
  if (cleanPhone.length === 10) {
    return `91${cleanPhone}`;
  }
  
  // For shorter numbers, this might not be a valid international number
  return cleanPhone;
};

/**
 * Format WhatsApp messages with styling (bold, italic, etc)
 * @param {string} text - Text content to format
 * @returns {JSX.Element} Formatted text with styling
 */
export const formatWhatsAppMessage = (text) => {
  // Handle undefined or null text
  if (!text) {
    return <Text as="span">No message content</Text>;
  }
  
  // Ensure text is a string
  const safeText = String(text);
  
  // Split text into lines and process each line
  return safeText.split('\n').map((line, lineIndex) => {
    let segments = [];
    let currentIndex = 0;

    // Process bold text (*text*)
    const boldRegex = /\*(.*?)\*/g;
    let boldMatch;
    while ((boldMatch = boldRegex.exec(line)) !== null) {
      // Add text before the bold section
      if (boldMatch.index > currentIndex) {
        segments.push(
          <Text as="span" key={`text-${currentIndex}`}>
            {line.slice(currentIndex, boldMatch.index)}
          </Text>
        );
      }
      // Add bold text
      segments.push(
        <Text as="span" fontWeight="bold" key={`bold-${boldMatch.index}`}>
          {boldMatch[1]}
        </Text>
      );
      currentIndex = boldMatch.index + boldMatch[0].length;
    }

    // Add remaining text after last bold section
    if (currentIndex < line.length) {
      const remainingText = line.slice(currentIndex);
      // Process italic text (_text_) in remaining text
      const italicRegex = /_(.*?)_/g;
      let lastIndex = 0;
      let italicMatch;
      
      while ((italicMatch = italicRegex.exec(remainingText)) !== null) {
        if (italicMatch.index > lastIndex) {
          segments.push(
            <Text as="span" key={`text-${currentIndex + lastIndex}`}>
              {remainingText.slice(lastIndex, italicMatch.index)}
            </Text>
          );
        }
        segments.push(
          <Text as="span" fontStyle="italic" key={`italic-${currentIndex + italicMatch.index}`}>
            {italicMatch[1]}
          </Text>
        );
        lastIndex = italicMatch.index + italicMatch[0].length;
      }
      
      if (lastIndex < remainingText.length) {
        segments.push(
          <Text as="span" key={`text-${currentIndex + lastIndex}`}>
            {remainingText.slice(lastIndex)}
          </Text>
        );
      }
    }

    // Return line with proper spacing
    return (
      <Text key={`line-${lineIndex}`} display="block">
        {segments.length > 0 ? segments : ' '}
      </Text>
    );
  });
};
